import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { TEMPLATE_CATEGORIES, DEFAULT_TEMPLATES, DEFAULT_STAMPS } from './constants';

const API = process.env.REACT_APP_BACKEND_URL;

export default function useGestionModeles({ getAuthHeader, userRole = 'super_admin' }) {
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [stamps, setStamps] = useState([]);
  const [_loadingStamps, setLoadingStamps] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showStampModal, setShowStampModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState('create');
  const [stampEditMode, setStampEditMode] = useState('create');
  const [editingStamp, setEditingStamp] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [editorForm, setEditorForm] = useState({
    name: '', category: 'courrier', description: '',
    stampId: null, includeSignatureBlock: true, sharedWith: []
  });
  const [stampForm, setStampForm] = useState({
    name: '', type: 'official', image: null, imagePreview: null, assignedTo: []
  });
  const [importProgress, setImportProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  
  const fileInputRef = useRef(null);
  const stampInputRef = useRef(null);

  // ===================== CHARGEMENT DES TAMPONS =====================
  const fetchStamps = async () => {
    try {
      setLoadingStamps(true);
      const response = await axios.get(`${API}/api/tampons`, { headers: getAuthHeader() });
      if (response.data && response.data.length > 0) {
        setStamps(response.data);
      } else {
        await initDefaultStamps();
      }
    } catch {
      setStamps(DEFAULT_STAMPS);
    } finally {
      setLoadingStamps(false);
    }
  };

  const initDefaultStamps = async () => {
    try {
      const response = await axios.post(`${API}/api/tampons/init-defaults`, {}, { headers: getAuthHeader() });
      if (response.data.initialized) {
        toast.success('Tampons par défaut initialisés');
        await fetchStamps();
      }
    } catch {
      setStamps(DEFAULT_STAMPS);
    }
  };

  useEffect(() => { fetchStamps(); }, []);

  // Statistiques
  const stats = useMemo(() => {
    const total = templates.length;
    const active = templates.filter(t => t.status === 'active').length;
    const archived = templates.filter(t => t.status === 'archived').length;
    const totalUsage = templates.reduce((sum, t) => sum + (t.usageCount || 0), 0);
    const byCategory = TEMPLATE_CATEGORIES.map(cat => ({
      ...cat, count: templates.filter(t => t.category === cat.id).length
    }));
    const mostUsed = [...templates].sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0)).slice(0, 5);
    return { total, active, archived, totalUsage, byCategory, mostUsed };
  }, [templates]);

  // Filtrage et tri
  const filteredTemplates = useMemo(() => {
    let filtered = [...templates];
    if (selectedCategory !== 'all') filtered = filtered.filter(t => t.category === selectedCategory);
    if (selectedStatus !== 'all') filtered = filtered.filter(t => t.status === selectedStatus);
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(search) ||
        t.description.toLowerCase().includes(search) ||
        t.category.toLowerCase().includes(search)
      );
    }
    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'name') comparison = a.name.localeCompare(b.name);
      else if (sortBy === 'usageCount') comparison = (a.usageCount || 0) - (b.usageCount || 0);
      else comparison = new Date(a.updatedAt) - new Date(b.updatedAt);
      return sortOrder === 'desc' ? -comparison : comparison;
    });
    return filtered;
  }, [templates, selectedCategory, selectedStatus, searchTerm, sortBy, sortOrder]);

  // Générer le bloc de signature avec QR code et tampon
  const generateSignatureBlock = (templateId, stampImage = null, stampName = null) => {
    const verificationId = `DOC-${templateId}-${Date.now().toString(36)}`.toUpperCase();
    const verificationUrl = `${window.location.origin}/verify/${verificationId}`;
    const timestamp = new Date().toLocaleString('fr-FR');
    const tamponHtml = stampImage 
      ? `<img src="${stampImage}" alt="${stampName || 'Tampon officiel'}" style="width: 100px; height: 100px; object-fit: contain;"/>`
      : `<span style="color: #999; font-size: 10px;">{{TAMPON}}</span>`;
    const tamponLabel = stampName || 'Tampon officiel';
    
    return `
<div style="margin-top: 40px; padding: 20px; border: 2px solid #1e3a5f; border-radius: 8px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);">
  <div style="display: flex; justify-content: space-between; align-items: flex-start;">
    <div style="flex: 1;">
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="M9 12l2 2 4-4"/>
        </svg>
        <span style="color: #22c55e; font-weight: bold;">DOCUMENT AUTHENTIFIÉ</span>
      </div>
      <p style="margin: 5px 0; font-size: 12px;"><strong>ID Vérification :</strong> <code style="background: #e9ecef; padding: 2px 6px; border-radius: 3px;">${verificationId}</code></p>
      <p style="margin: 5px 0; font-size: 12px;"><strong>Date/Heure :</strong> ${timestamp}</p>
      <p style="margin: 5px 0; font-size: 12px;"><strong>Émis par :</strong> O'SCOP</p>
    </div>
    <div style="text-align: center;">
      <div style="background: white; padding: 8px; border-radius: 4px; border: 1px solid #ddd;">
        <img src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(verificationUrl)}" alt="QR Code" style="width: 80px; height: 80px;"/>
      </div>
      <p style="font-size: 10px; color: #666; margin-top: 5px;">Scanner pour vérifier</p>
    </div>
  </div>
  <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #ddd;">
    <div style="display: flex; gap: 20px;">
      <div style="flex: 1;">
        <p style="font-size: 11px; color: #000000; font-weight: bold; margin-bottom: 5px;">${tamponLabel}</p>
        <div style="width: 100px; height: 100px; border: 1px dashed #ccc; border-radius: 50%; display: flex; align-items: center; justify-content: center; overflow: hidden;">
          ${tamponHtml}
        </div>
      </div>
      <div style="flex: 1;">
        <p style="font-size: 11px; color: #000000; font-weight: bold; margin-bottom: 5px;">Signature</p>
        <div style="height: 60px; border-bottom: 1px solid #333; width: 200px;"></div>
        <p style="font-size: 11px; color: #000000; margin-top: 5px;">{{signataire_nom}}</p>
      </div>
    </div>
  </div>
</div>`;
  };

  // Ouvrir l'éditeur
  const openEditor = (template = null, mode = 'create') => {
    setEditMode(mode);
    if (template) {
      setSelectedTemplate(template);
      setEditorContent(template.content);
      setEditorForm({
        name: mode === 'duplicate' ? `${template.name} (copie)` : template.name,
        category: template.category, description: template.description,
        stampId: template.stampId,
        includeSignatureBlock: template.content.includes('{{BLOC_SIGNATURE}}'),
        sharedWith: template.sharedWith || []
      });
    } else {
      setSelectedTemplate(null);
      setEditorContent('');
      setEditorForm({
        name: '', category: 'courrier', description: '',
        stampId: null, includeSignatureBlock: true, sharedWith: []
      });
    }
    setShowEditorModal(true);
  };

  // Sauvegarder le modèle
  const saveTemplate = () => {
    if (!editorForm.name || !editorContent) {
      toast.error('Veuillez remplir le nom et le contenu');
      return;
    }
    let finalContent = editorContent;
    if (editorForm.includeSignatureBlock && !finalContent.includes('{{BLOC_SIGNATURE}}')) {
      finalContent += '\n{{BLOC_SIGNATURE}}';
    }
    if (editMode === 'edit' && selectedTemplate) {
      setTemplates(templates.map(t => 
        t.id === selectedTemplate.id 
          ? { ...t, ...editorForm, content: finalContent, updatedAt: new Date().toISOString().split('T')[0] } 
          : t
      ));
      toast.success('Modèle mis à jour');
    } else {
      const newTemplate = {
        id: Date.now(), ...editorForm, content: finalContent, status: 'draft',
        createdBy: userRole, createdAt: new Date().toISOString().split('T')[0],
        updatedAt: new Date().toISOString().split('T')[0], usageCount: 0
      };
      setTemplates([...templates, newTemplate]);
      toast.success(editMode === 'duplicate' ? 'Modèle dupliqué' : 'Modèle créé');
    }
    setShowEditorModal(false);
  };

  const changeStatus = (id, newStatus) => {
    setTemplates(templates.map(t => 
      t.id === id ? { ...t, status: newStatus, updatedAt: new Date().toISOString().split('T')[0] } : t
    ));
    toast.success(`Modèle ${newStatus === 'active' ? 'activé' : newStatus === 'archived' ? 'archivé' : 'désactivé'}`);
  };

  const deleteTemplate = (id) => {
    if (!window.confirm('Supprimer définitivement ce modèle ?')) return;
    setTemplates(templates.filter(t => t.id !== id));
    toast.success('Modèle supprimé');
  };

  // Export PDF
  const exportToPDF = async (template) => {
    toast.info('Génération du PDF en cours...');
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      const container = document.createElement('div');
      container.innerHTML = template.content.replace('{{BLOC_SIGNATURE}}', generateSignatureBlock(template.id));
      container.style.width = '210mm';
      container.style.padding = '20mm';
      container.style.background = 'white';
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      document.body.appendChild(container);
      const canvas = await html2canvas(container, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`${template.name}.pdf`);
      document.body.removeChild(container);
      toast.success('PDF téléchargé');
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const exportToHTML = (template) => {
    const content = template.content.replace('{{BLOC_SIGNATURE}}', generateSignatureBlock(template.id));
    const fullHtml = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>${template.name}</title><style>body { font-family: Arial, sans-serif; margin: 0; padding: 20px; } @media print { body { margin: 0; } }</style></head><body>${content}</body></html>`;
    const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' });
    saveAs(blob, `${template.name}.html`);
    toast.success('HTML téléchargé');
  };

  const printTemplate = (template) => {
    const printWindow = window.open('', '_blank');
    const content = template.content.replace('{{BLOC_SIGNATURE}}', generateSignatureBlock(template.id));
    printWindow.document.write(`<html><head><title>${template.name}</title><style>@media print { body { margin: 0; } } body { font-family: Arial, sans-serif; }</style></head><body>${content}</body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  // Import fichier multi-format
  const handleFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsImporting(true);
    setImportProgress(10);
    try {
      const fileName = file.name.toLowerCase();
      let htmlContent = '';
      let templateName = file.name.replace(/\.[^/.]+$/, '');

      if (fileName.endsWith('.html') || fileName.endsWith('.htm')) {
        setImportProgress(50);
        htmlContent = await file.text();
        toast.success('Fichier HTML importé');
      } else if (fileName.endsWith('.docx')) {
        setImportProgress(30);
        const arrayBuffer = await file.arrayBuffer();
        setImportProgress(60);
        const result = await mammoth.convertToHtml({ arrayBuffer });
        htmlContent = `<div style="font-family: Arial, sans-serif; padding: 20px;">${result.value}</div>`;
        toast.success('Document Word importé et converti en HTML');
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        setImportProgress(30);
        const arrayBuffer = await file.arrayBuffer();
        setImportProgress(50);
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        setImportProgress(70);
        const htmlTable = XLSX.utils.sheet_to_html(worksheet, { editable: false });
        htmlContent = `<div style="font-family: Arial, sans-serif; padding: 20px;"><h2 style="color: #1e3a5f;">${templateName}</h2>${htmlTable}</div>`;
        toast.success('Fichier Excel importé et converti en tableau HTML');
      } else if (/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(fileName)) {
        setImportProgress(50);
        const reader = new FileReader();
        htmlContent = await new Promise((resolve) => {
          reader.onload = (event) => {
            resolve(`<div style="font-family: Arial, sans-serif; padding: 20px; text-align: center;"><h2 style="color: #1e3a5f;">${templateName}</h2><img src="${event.target.result}" alt="${templateName}" style="max-width: 100%; height: auto; border: 1px solid #ddd; border-radius: 8px;" /></div>`);
          };
          reader.readAsDataURL(file);
        });
        toast.success('Image importée');
      } else if (fileName.endsWith('.csv')) {
        setImportProgress(30);
        const text = await file.text();
        setImportProgress(50);
        const workbook = XLSX.read(text, { type: 'string' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        setImportProgress(70);
        const htmlTable = XLSX.utils.sheet_to_html(worksheet, { editable: false });
        htmlContent = `<div style="font-family: Arial, sans-serif; padding: 20px;"><h2 style="color: #1e3a5f;">${templateName}</h2>${htmlTable}</div>`;
        toast.success('Fichier CSV importé et converti en tableau');
      } else if (fileName.endsWith('.txt')) {
        setImportProgress(50);
        const text = await file.text();
        htmlContent = `<div style="font-family: Arial, sans-serif; padding: 20px;"><h2 style="color: #1e3a5f;">${templateName}</h2><pre style="white-space: pre-wrap; background: #f8f9fa; padding: 15px; border-radius: 8px;">${text}</pre></div>`;
        toast.success('Fichier texte importé');
      } else {
        toast.error('Format non supporté. Formats acceptés: HTML, Word (.docx), Excel (.xlsx, .xls), Images, CSV, TXT');
        setIsImporting(false);
        setImportProgress(0);
        return;
      }

      setImportProgress(90);
      setEditorContent(htmlContent);
      setEditorForm(prev => ({ ...prev, name: templateName, description: `Importé depuis ${file.name}` }));
      setEditMode('create');
      setImportProgress(100);
      setTimeout(() => {
        setShowEditorModal(true);
        setIsImporting(false);
        setImportProgress(0);
      }, 500);
    } catch (error) {
      toast.error(`Erreur lors de l'import: ${error.message}`);
      setIsImporting(false);
      setImportProgress(0);
    }
    e.target.value = '';
  };

  // ==================== GESTION DES TAMPONS ====================
  const resetStampForm = () => {
    setStampForm({ name: '', type: 'official', image: null, imagePreview: null, assignedTo: [] });
    setEditingStamp(null);
    setStampEditMode('create');
  };

  const openStampModal = () => { resetStampForm(); setShowStampModal(true); };

  const openEditStamp = (stamp) => {
    setStampForm({
      name: stamp.name, type: stamp.type,
      image: stamp.image, imagePreview: stamp.image,
      assignedTo: stamp.assignedTo || []
    });
    setEditingStamp(stamp);
    setStampEditMode('edit');
    setShowStampModal(true);
  };

  const handleStampImageImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Veuillez sélectionner une image'); return; }
    const reader = new FileReader();
    reader.onload = (event) => {
      setStampForm(prev => ({ ...prev, image: event.target.result, imagePreview: event.target.result }));
      toast.success('Image chargée');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const toggleStampCategory = (categoryId) => {
    setStampForm(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.includes(categoryId)
        ? prev.assignedTo.filter(c => c !== categoryId)
        : [...prev.assignedTo, categoryId]
    }));
  };

  const saveStamp = async () => {
    if (!stampForm.name) { toast.error('Veuillez entrer un nom pour le tampon'); return; }
    try {
      if (stampEditMode === 'edit' && editingStamp) {
        const response = await axios.put(`${API}/api/tampons/${editingStamp.id}`, {
          name: stampForm.name, type: stampForm.type,
          image: stampForm.image || stampForm.imagePreview, assignedTo: stampForm.assignedTo
        }, { headers: getAuthHeader() });
        setStamps(stamps.map(s => s.id === editingStamp.id ? response.data.tampon : s));
        toast.success('Tampon modifié avec succès');
      } else {
        const response = await axios.post(`${API}/api/tampons`, {
          name: stampForm.name, type: stampForm.type,
          image: stampForm.image || stampForm.imagePreview, assignedTo: stampForm.assignedTo
        }, { headers: getAuthHeader() });
        setStamps([...stamps, response.data.tampon]);
        toast.success('Tampon créé avec succès');
      }
      setShowStampModal(false);
      resetStampForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde du tampon');
    }
  };

  const deleteStamp = async (id) => {
    if (!window.confirm('Supprimer définitivement ce tampon ?')) return;
    try {
      await axios.delete(`${API}/api/tampons/${id}`, { headers: getAuthHeader() });
      setStamps(stamps.filter(s => s.id !== id));
      toast.success('Tampon supprimé avec succès');
    } catch {
      toast.error('Erreur lors de la suppression du tampon');
    }
  };

  const shareTemplate = (templateId, recipients) => {
    setTemplates(templates.map(t => t.id === templateId ? { ...t, sharedWith: recipients } : t));
    toast.success('Partage mis à jour');
  };

  // Preview avec contenu remplacé
  const getPreviewContent = (template) => {
    let content = template.content;
    let stampImage = null;
    let stampName = null;
    if (template.stampId) {
      let stamp = stamps.find(s => s.id === template.stampId);
      if (!stamp && typeof template.stampId === 'number' && stamps[template.stampId - 1]) {
        stamp = stamps[template.stampId - 1];
      }
      if (!stamp && stamps.length > 0) stamp = stamps[0];
      if (stamp) { stampImage = stamp.image; stampName = stamp.name; }
    }
    content = content.replace('{{BLOC_SIGNATURE}}', generateSignatureBlock(template.id, stampImage, stampName));
    if (stampImage) {
      const tamponHtml = `<img src="${stampImage}" alt="${stampName || 'Tampon officiel'}" style="width: 100px; height: 100px; object-fit: contain; border-radius: 50%;" onerror="this.style.display='none'"/>`;
      content = content.replace(/{{TAMPON}}/g, tamponHtml);
    } else {
      content = content.replace(/{{TAMPON}}/g, `<div style="width: 100px; height: 100px; border: 2px dashed #ccc; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #999; font-size: 10px;">Pas de tampon</div>`);
    }
    const replacements = {
      '{{date}}': new Date().toLocaleDateString('fr-FR'),
      '{{reference}}': 'REF-2024-00123', '{{nom}}': 'DUPONT', '{{prenom}}': 'Jean',
      '{{adresse}}': '123 Rue de la Paix', '{{telephone}}': '01 23 45 67 89',
      '{{email}}': 'jean.dupont@email.fr', '{{lieu}}': 'Paris',
      '{{signataire}}': "Direction O'SCOP", '{{signataire_nom}}': 'M. Martin',
    };
    Object.entries(replacements).forEach(([key, value]) => {
      content = content.replace(new RegExp(key, 'g'), value);
    });
    return content;
  };

  return {
    templates,
    stamps,
    activeTab,
    setActiveTab,
    selectedCategory,
    setSelectedCategory,
    selectedStatus,
    setSelectedStatus,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    showEditorModal,
    setShowEditorModal,
    showPreviewModal,
    setShowPreviewModal,
    showShareModal,
    setShowShareModal,
    showStampModal,
    setShowStampModal,
    showStatsModal,
    setShowStatsModal,
    selectedTemplate,
    setSelectedTemplate,
    editMode,
    stampEditMode,
    editorContent,
    setEditorContent,
    editorForm,
    setEditorForm,
    stampForm,
    setStampForm,
    importProgress,
    isImporting,
    fileInputRef,
    stampInputRef,
    stats,
    filteredTemplates,
    generateSignatureBlock,
    openEditor,
    saveTemplate,
    changeStatus,
    deleteTemplate,
    exportToPDF,
    exportToHTML,
    printTemplate,
    resetStampForm,
    openStampModal,
    openEditStamp,
    handleStampImageImport,
    toggleStampCategory,
    saveStamp,
    deleteStamp,
    shareTemplate,
    getPreviewContent,
    handleFileImport,
  };
}
